"use client";

import React, { useEffect } from "react";
import { Card, Switch, Spin, Alert, Divider } from "antd";
import useSettingsStore from "./SettingsStore.ts";
import useLanguageStore from "../../stores/languageStore.ts";

export default function Settings() {
    const {
        userLimitsData,
        placeAutoApproveData,
        isLoading,
        isSaving,
        isSavingPlaceConfig,
        getUserLimitsStatus,
        updateUserLimits,
        getPlaceAutoApproveConfig,
        togglePlaceAutoApprove,
    } = useSettingsStore();

    const { t } = useLanguageStore();

    useEffect(() => {
        getUserLimitsStatus();
        getPlaceAutoApproveConfig();
    }, [getUserLimitsStatus, getPlaceAutoApproveConfig]);

    const handleToggleUserLimits = async (checked: boolean) => {
        await updateUserLimits(checked);
    };

    const handleTogglePlaceAutoApprove = async (checked: boolean) => {
        await togglePlaceAutoApprove(checked);
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <Spin size="large" tip={t("common.loading")} />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="mb-8">
                <div className="flex items-center gap-2 text-sm text-gray-400 mb-2 font-medium">
                    <span>{t("common.home")}</span>
                    <span>/</span>
                    <span className="text-black">{t("navigation.settings")}</span>
                </div>
                <h1 className="text-3xl font-bold text-black">{t("settings.title")}</h1>
                <p className="text-gray-500">{t("settings.subtitle")}</p>
            </div>

            {/* User Limits Settings Card */}
            <Card
                className="shadow-soft rounded-3xl border-gray-100"
                title={
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <i className="fa-solid fa-user-lock text-blue-600 text-lg"></i>
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-black m-0">{t("settings.userLimits.title")}</h3>
                            <p className="text-sm text-gray-500 font-normal m-0">
                                {t("settings.userLimits.subtitle")}
                            </p>
                        </div>
                    </div>
                }
            >
                <div className="space-y-4">
                    <Alert
                        message={t("settings.userLimits.aboutTitle")}
                        description={
                            <div className="space-y-2">
                                <p className="mb-2">
                                    <strong>{t("settings.userLimits.enabled")}:</strong> {t("settings.userLimits.enabledDescription")}
                                </p>
                                <p className="mb-0">
                                    <strong>{t("settings.userLimits.disabled")}:</strong> {t("settings.userLimits.disabledDescription")}
                                </p>
                            </div>
                        }
                        type="info"
                        showIcon
                        icon={<i className="fa-solid fa-circle-info"></i>}
                        className="rounded-xl"
                    />

                    <Divider className="my-4" />

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${userLimitsData?.enabled ? 'bg-green-100' : 'bg-red-100'
                                }`}>
                                <i className={`fa-solid ${userLimitsData?.enabled ? 'fa-lock text-green-600' : 'fa-lock-open text-red-600'
                                    } text-xl`}></i>
                            </div>
                            <div>
                                <div className="font-semibold text-gray-900 text-base">
                                    {t("settings.userLimits.statusTitle")}
                                </div>
                                <div className="text-sm text-gray-500">
                                    {userLimitsData?.enabled
                                        ? t("settings.userLimits.limitsActive")
                                        : t("settings.userLimits.limitsDisabled")}
                                </div>
                            </div>
                        </div>
                        <Switch
                            checked={userLimitsData?.enabled || false}
                            onChange={handleToggleUserLimits}
                            loading={isSaving}
                            checkedChildren={<i className="fa-solid fa-check"></i>}
                            unCheckedChildren={<i className="fa-solid fa-xmark"></i>}
                            className="bg-gray-300"
                        />
                    </div>

                    {userLimitsData?.enabled && (
                        <Alert
                            message={t("settings.userLimits.alertActiveTitle")}
                            description={t("settings.userLimits.alertActiveDescription")}
                            type="success"
                            showIcon
                            icon={<i className="fa-solid fa-circle-check"></i>}
                            className="rounded-xl"
                        />
                    )}

                    {!userLimitsData?.enabled && (
                        <Alert
                            message={t("settings.userLimits.alertDisabledTitle")}
                            description={t("settings.userLimits.alertDisabledDescription")}
                            type="warning"
                            showIcon
                            icon={<i className="fa-solid fa-triangle-exclamation"></i>}
                            className="rounded-xl"
                        />
                    )}
                </div>
            </Card>

            {/* Place Auto Approve Settings Card */}
            <Card
                className="shadow-soft rounded-3xl border-gray-100"
                title={
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                            <i className="fa-solid fa-check-circle text-green-600 text-lg"></i>
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-black m-0">Place Auto Approve</h3>
                            <p className="text-sm text-gray-500 font-normal m-0">
                                Tự động phê duyệt địa điểm khi được tạo
                            </p>
                        </div>
                    </div>
                }
            >
                <div className="space-y-4">
                    <Alert
                        message="Về tính năng Auto Approve"
                        description={
                            <div className="space-y-2">
                                <p className="mb-2">
                                    <strong>Bật:</strong> Tất cả địa điểm được tạo sẽ tự động được phê duyệt (status = APPROVED)
                                </p>
                                <p className="mb-0">
                                    <strong>Tắt:</strong> Chỉ admin và premium users được auto-approve, còn lại ở trạng thái WAITING
                                </p>
                            </div>
                        }
                        type="info"
                        showIcon
                        icon={<i className="fa-solid fa-circle-info"></i>}
                        className="rounded-xl"
                    />

                    <Divider className="my-4" />

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${placeAutoApproveData?.auto_approve ? 'bg-green-100' : 'bg-red-100'
                                }`}>
                                <i className={`fa-solid ${placeAutoApproveData?.auto_approve
                                        ? 'fa-check-circle text-green-600'
                                        : 'fa-clock text-red-600'
                                    } text-xl`}></i>
                            </div>
                            <div>
                                <div className="font-semibold text-gray-900 text-base">
                                    Trạng thái Auto Approve
                                </div>
                                <div className="text-sm text-gray-500">
                                    {placeAutoApproveData?.auto_approve
                                        ? "Đang bật - Tất cả địa điểm được tự động phê duyệt"
                                        : "Đang tắt - Chỉ admin/premium được auto-approve"}
                                </div>
                            </div>
                        </div>
                        <Switch
                            checked={placeAutoApproveData?.auto_approve || false}
                            onChange={handleTogglePlaceAutoApprove}
                            loading={isSavingPlaceConfig}
                            checkedChildren={<i className="fa-solid fa-check"></i>}
                            unCheckedChildren={<i className="fa-solid fa-xmark"></i>}
                            className="bg-gray-300"
                        />
                    </div>

                    {placeAutoApproveData?.auto_approve && (
                        <Alert
                            message="Auto Approve đang bật"
                            description="Tất cả địa điểm mới được tạo sẽ tự động được phê duyệt ngay lập tức."
                            type="success"
                            showIcon
                            icon={<i className="fa-solid fa-circle-check"></i>}
                            className="rounded-xl"
                        />
                    )}

                    {!placeAutoApproveData?.auto_approve && (
                        <Alert
                            message="Auto Approve đang tắt"
                            description="Chỉ admin và premium users được auto-approve. Các users khác cần chờ admin phê duyệt."
                            type="warning"
                            showIcon
                            icon={<i className="fa-solid fa-triangle-exclamation"></i>}
                            className="rounded-xl"
                        />
                    )}
                </div>
            </Card>

            {/* Future Settings Sections */}
            <Card
                className="shadow-soft rounded-3xl border-gray-100 opacity-50"
                title={
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                            <i className="fa-solid fa-cog text-purple-600 text-lg"></i>
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-black m-0">{t("settings.generalSettings.title")}</h3>
                            <p className="text-sm text-gray-500 font-normal m-0">
                                {t("settings.generalSettings.comingSoon")}
                            </p>
                        </div>
                    </div>
                }
            >
                <div className="text-center py-8 text-gray-400">
                    <i className="fa-solid fa-wrench text-4xl mb-3"></i>
                    <p>{t("settings.generalSettings.additionalSettings")}</p>
                </div>
            </Card>
        </div>
    );
}
